import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { SKIP_PENDING_MUTATION_GUARD } from '@application/data-access';
import {
  IWebPushSubscribeRequest,
  IWebPushSubscribeResponse,
  IWebPushVapidPublicKeyResponse,
} from '../entities/web-push.model';

/**
 * HTTP do Web Push da recusa: a inscrição do navegador no accessdirectoryservice
 * e a chave VAPID no accessedgemanager. Tudo corre em segundo plano, sem a
 * recepção ter pedido nada: por isso nenhuma chamada segura o aviso de saída da
 * página (`SKIP_PENDING_MUTATION_GUARD`).
 */
@Injectable({ providedIn: 'root' })
export class WebPushSubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;
  private readonly background = new HttpContext().set(
    SKIP_PENDING_MUTATION_GUARD,
    true
  );

  /** Onde o service worker libera pelo token do push — sem bearer. */
  public get releaseUrl(): string {
    return `${this.apiUrl.access_edge_manager_service}/web-push/override-denied`;
  }

  /**
   * Anónimo: sem `withCredentials`, sem bearer. 404 quer dizer que o ambiente não
   * tem Web Push, e a notificação continua a ser só a da aba.
   */
  public getVapidPublicKey(): Observable<IWebPushVapidPublicKeyResponse> {
    return this.http.get<IWebPushVapidPublicKeyResponse>(
      `${this.apiUrl.access_edge_manager_service}/web-push/vapid-public-key`
    );
  }

  public subscribe(
    body: IWebPushSubscribeRequest
  ): Observable<IWebPushSubscribeResponse> {
    return this.http.post<IWebPushSubscribeResponse>(
      `${this.apiUrl.access_directory_service}/web-push/subscriptions`,
      body,
      { context: this.background, withCredentials: true }
    );
  }

  /**
   * Pelo endpoint, no corpo e não na query: o endereço identifica o navegador da
   * pessoa e não deve parar em log de acesso. 204 mesmo que já não exista.
   */
  public unsubscribeByEndpoint(endpoint: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.access_directory_service}/web-push/subscriptions`,
      {
        body: { Endpoint: endpoint },
        context: this.background,
        withCredentials: true,
      }
    );
  }
}
